// ── Debug tool definitions ────────────────────────────────────────────────────
export const DEBUG_HOVER_TOOLS = [
  { key: 'cssSource',   label: 'CSS Source Inspector',   sub: 'Shows matched CSS selectors on hover (file name shown if externally linked)',        lsKey: 'dawn.debug.cssSource'   },
  { key: 'colorHover',  label: 'Color on Hover',         sub: 'Displays computed color and background-color values',     lsKey: 'dawn.debug.colorHover'  },
  { key: 'spacingViz',  label: 'Spacing Visualizer',     sub: 'Shows margin and padding box model on hovered element',   lsKey: 'dawn.debug.spacingViz'  },
  { key: 'zIndex',      label: 'z-index Inspector',      sub: 'Shows z-index chain walking up the DOM tree',             lsKey: 'dawn.debug.zIndex'      },
  { key: 'fontInspect', label: 'Font Inspector',         sub: 'Displays font family, size, weight, and line-height',     lsKey: 'dawn.debug.fontInspect' },
  { key: 'cssVars',     label: 'CSS Variable Inspector', sub: 'Shows active --custom-properties on the hovered element', lsKey: 'dawn.debug.cssVars'     },
  { key: 'securityRole', label: 'Security Role Inspector', sub: 'Shows current user roles and any data-role/data-permission attributes on the hovered element', lsKey: 'dawn.debug.securityRole' },
];

export const DEBUG_OVERLAY_TOOLS = [
  { key: 'componentBoundary', label: 'Component Boundary Overlay', sub: 'Outlines React component-level elements across the page',          lsKey: 'dawn.debug.componentBoundary' },
  { key: 'tabOrder',          label: 'Tab Order Visualizer',       sub: 'Numbers all focusable elements in tab sequence',                   lsKey: 'dawn.debug.tabOrder'          },
  { key: 'gridFlex',          label: 'Grid/Flex Visualizer',       sub: 'Highlights flex (blue) and grid (green) containers',               lsKey: 'dawn.debug.gridFlex'          },
  { key: 'breakpoint',        label: 'Breakpoint Indicator',       sub: 'Fixed corner badge showing current responsive breakpoint',          lsKey: 'dawn.debug.breakpoint'        },
  { key: 'classInventory',    label: 'Class Inventory',            sub: 'Lists every CSS class in the DOM with counts; click to highlight',  lsKey: 'dawn.debug.classInventory'    },
];

// ── State helpers ─────────────────────────────────────────────────────────────
export const SHOW_DEBUG_KEY = 'dawn.debug.showPanel';

export const isAnyDebugToolActive = () =>
  [...DEBUG_HOVER_TOOLS, ...DEBUG_OVERLAY_TOOLS].some(t => localStorage.getItem(t.lsKey) === 'true');

export const isDebugPanelVisible = () =>
  localStorage.getItem(SHOW_DEBUG_KEY) === 'true';

export const notifyDebugChanged = () =>
  window.dispatchEvent(new Event('dawn-debug-changed'));

// ── Tooltip (hover inspectors) ────────────────────────────────────────────────
const TOOLTIP_ID = 'dawn-debug-tooltip';

// Cache source-map decoding — parsing base64 on every mouseover would be brutal
const _sheetSourceCache = new WeakMap();

const getSheetSourceFile = (sheet) => {
  if (_sheetSourceCache.has(sheet)) return _sheetSourceCache.get(sheet);
  let result = null;
  if (sheet.href) {
    result = sheet.href.split('/').pop().split('?')[0];
  } else {
    // CRA dev mode: css-loader embeds an inline base64 source map in each <style> tag.
    // Decode it to get the original file name.
    try {
      const text = sheet.ownerNode?.textContent || '';
      const m = text.match(/\/\*#\s*sourceMappingURL=data:application\/json;(?:charset=utf-8;)?base64,([A-Za-z0-9+/=]+)/);
      if (m) {
        const map = JSON.parse(atob(m[1]));
        if (map.sources?.length > 0) {
          // source entry looks like "webpack://pmd-frontend/./src/App.css"
          result = map.sources[0]
            .replace(/^webpack:\/\/[^/]+\//, '')
            .replace(/^\.\//, '')
            .split('/').pop();
        }
      }
    } catch (e) {}
  }
  _sheetSourceCache.set(sheet, result);
  return result;
};

const getCssSources = (el) => {
  const byFile   = {};  // fileName -> [selector, ...]
  const unknown  = [];  // selectors where file couldn't be determined
  try {
    for (const sheet of document.styleSheets) {
      const fileName = getSheetSourceFile(sheet);
      try {
        for (const rule of (sheet.cssRules || [])) {
          if (rule.selectorText) {
            try {
              if (el.matches(rule.selectorText)) {
                if (fileName) {
                  (byFile[fileName] = byFile[fileName] || []).push(rule.selectorText);
                } else {
                  unknown.push(rule.selectorText);
                }
              }
            } catch (e) { /* invalid selector */ }
          }
        }
      } catch (e) { /* cross-origin */ }
    }
  } catch (e) {}
  // Trim per-file selector lists so tooltip doesn't get huge
  for (const f of Object.keys(byFile)) byFile[f] = byFile[f].slice(0, 6);
  return { byFile, unknown: unknown.slice(0, 6) };
};

// Mode: 'scope' — vars available/inherited via getComputedStyle (shows full --var palette)
const getCssVarsInScope = (el) => {
  const vars = {};
  try {
    const cs = window.getComputedStyle(el);
    for (let i = 0; i < cs.length; i++) {
      const p = cs[i];
      if (p.startsWith('--')) vars[p] = cs.getPropertyValue(p).trim();
    }
  } catch (e) {}
  return Object.entries(vars).slice(0, 12);
};

// Mode: 'used' — vars referenced via var(--x) in rules that match the element
const getCssVarsUsed = (el) => {
  const found = {};
  const varRe = /var\(\s*(--[\w-]+)/g;
  try {
    for (const sheet of document.styleSheets) {
      try {
        for (const rule of (sheet.cssRules || [])) {
          if (rule.selectorText && rule.style) {
            try {
              if (el.matches(rule.selectorText)) {
                for (let i = 0; i < rule.style.length; i++) {
                  const val = rule.style.getPropertyValue(rule.style[i]);
                  let m;
                  while ((m = varRe.exec(val)) !== null) {
                    const name = m[1];
                    if (!(name in found)) {
                      const resolved = window.getComputedStyle(el).getPropertyValue(name).trim();
                      found[name] = resolved || '(not resolved)';
                    }
                  }
                }
              }
            } catch (e) {}
          }
        }
      } catch (e) {}
    }
  } catch (e) {}
  return Object.entries(found).slice(0, 12);
};

export const CSS_VARS_MODE_KEY = 'dawn.debug.cssVarsMode';

const buildTooltipContent = (el) => {
  const cs = window.getComputedStyle(el);
  const sections = [];
  const active = (key) => localStorage.getItem(key) === 'true';

  if (active('dawn.debug.cssSource')) {
    const { byFile, unknown } = getCssSources(el);
    const lines = [];
    for (const [file, sels] of Object.entries(byFile)) {
      lines.push(`<span style="color:#fbbf24;font-weight:600">${file}</span>`);
      sels.forEach(s => lines.push(`&nbsp;&nbsp;<span style="color:#86efac">${s}</span>`));
    }
    if (unknown.length) {
      lines.push(`<span style="color:#94a3b8">(source unknown)</span>`);
      unknown.forEach(s => lines.push(`&nbsp;&nbsp;<span style="color:#86efac">${s}</span>`));
    }
    sections.push(
      `<b style="color:#818cf8">CSS Source</b><br>` +
      (lines.length ? lines.join('<br>') : '<i style="color:#94a3b8">none matched</i>')
    );
  }
  if (active('dawn.debug.colorHover')) {
    sections.push(`<b style="color:#818cf8">Colors</b><br>color: ${cs.color}<br>bg: ${cs.backgroundColor}`);
  }
  if (active('dawn.debug.spacingViz')) {
    sections.push(`<b style="color:#818cf8">Spacing</b><br>margin: ${cs.marginTop} ${cs.marginRight} ${cs.marginBottom} ${cs.marginLeft}<br>padding: ${cs.paddingTop} ${cs.paddingRight} ${cs.paddingBottom} ${cs.paddingLeft}`);
  }
  if (active('dawn.debug.zIndex')) {
    const chain = [];
    let node = el;
    while (node && node !== document.documentElement) {
      const z = window.getComputedStyle(node).zIndex;
      if (z !== 'auto') chain.push(z);
      node = node.parentElement;
    }
    sections.push(`<b style="color:#818cf8">z-index</b><br>${chain.length ? chain.join(' → ') : 'auto'}`);
  }
  if (active('dawn.debug.fontInspect')) {
    sections.push(`<b style="color:#818cf8">Font</b><br>${cs.fontFamily.split(',')[0].replace(/['"]/g, '')}<br>${cs.fontSize} / wt:${cs.fontWeight} / lh:${cs.lineHeight}`);
  }
  if (active('dawn.debug.cssVars')) {
    const mode = localStorage.getItem(CSS_VARS_MODE_KEY) || 'used';
    const vars = mode === 'scope' ? getCssVarsInScope(el) : getCssVarsUsed(el);
    const modeLabel = mode === 'scope' ? 'in scope' : 'used';
    sections.push(
      `<b style="color:#818cf8">CSS Vars</b><span style="color:#94a3b8;font-size:9px;margin-left:4px;">(${modeLabel})</span><br>` +
      (vars.length ? vars.map(([k, v]) => `${k}: <span style="color:#86efac">${v}</span>`).join('<br>') : '<i style="color:#94a3b8">none found</i>')
    );
  }
  if (active('dawn.debug.securityRole')) {
    const auth = window.__dawnDebugAuth;
    const lines = [];
    if (auth) {
      lines.push(`<span style="color:#f9a8d4">${auth.name || auth.email || '(unknown)'}</span>`);
      if (auth.email) lines.push(`<span style="color:#94a3b8">${auth.email}</span>`);
      if (auth.isSysAdmin) lines.push(`<span style="color:#fbbf24">SysAdmin</span>`);
      const roles = auth.roles || {};
      const roleEntries = Object.entries(roles);
      if (roleEntries.length) {
        lines.push(`<span style="color:#94a3b8;font-size:9px">ROLES</span>`);
        roleEntries.forEach(([db, role]) => lines.push(`&nbsp;&nbsp;<span style="color:#86efac">${db}</span>: ${role}`));
      } else if (!auth.isSysAdmin) {
        lines.push(`<span style="color:#94a3b8">no DB roles</span>`);
      }
      if (auth.isImpersonating && auth.realName) {
        lines.push(`<span style="color:#fb923c">impersonating — real: ${auth.realName}</span>`);
      }
    } else {
      lines.push(`<i style="color:#94a3b8">not authenticated</i>`);
    }
    const dataRole = el.getAttribute('data-role');
    const dataPerm = el.getAttribute('data-permission');
    if (dataRole || dataPerm) {
      lines.push(`<span style="color:#94a3b8;font-size:9px">ELEMENT ATTRS</span>`);
      if (dataRole) lines.push(`&nbsp;&nbsp;data-role: <span style="color:#86efac">${dataRole}</span>`);
      if (dataPerm) lines.push(`&nbsp;&nbsp;data-permission: <span style="color:#86efac">${dataPerm}</span>`);
    }
    // Nav item: the row's own key. The server decided the row is in this person's tree, so they may open it.
    const navEl = el.closest('[data-nav-id]');
    const navId = navEl?.getAttribute('data-nav-id');
    if (navId) {
      const navKey = navEl.getAttribute('data-nav-key');
      lines.push(`<span style="color:#94a3b8;font-size:9px">PAGE ACCESS (${navId})</span>`);
      lines.push(navKey
        ? `&nbsp;&nbsp;key: <span style="color:#fbbf24">${navKey}</span>`
        : `&nbsp;&nbsp;<span style="color:#86efac">no key of its own (opens with what is under it, or its table)</span>`);
      lines.push(`&nbsp;&nbsp;<span style="color:#86efac">✓ in this person's navigation</span>`);
    }
    sections.push(`<b style="color:#818cf8">Security Role</b><br>` + lines.join('<br>'));
  }

  return sections.join('<hr style="border:none;border-top:1px solid rgba(255,255,255,0.15);margin:5px 0">');
};

const ensureTooltip = () => {
  let tip = document.getElementById(TOOLTIP_ID);
  if (!tip) {
    tip = document.createElement('div');
    tip.id = TOOLTIP_ID;
    tip.className = 'dawn-debug-tooltip';
    document.body.appendChild(tip);
  }
  return tip;
};

const _debugMouseMove = (e) => {
  const tip = document.getElementById(TOOLTIP_ID);
  if (tip) {
    tip.style.left = `${Math.min(e.clientX + 16, window.innerWidth - 300)}px`;
    tip.style.top  = `${Math.min(e.clientY + 16, window.innerHeight - 160)}px`;
  }
};

const _debugMouseOver = (e) => {
  const tip = document.getElementById(TOOLTIP_ID);
  if (!tip) return;
  const el = e.target;
  if (el === tip || tip.contains(el)) return;
  if (el.closest('.footer-debug-wrap')) { tip.style.display = 'none'; return; }
  // Store which element we're inspecting so the mode-toggle can re-render
  tip._dawnTarget = el;
  const content = buildTooltipContent(el);
  if (content) { tip.innerHTML = content; tip.style.display = 'block'; }
  else          { tip.style.display = 'none'; }
};


export const syncHoverState = () => {
  const anyHover = DEBUG_HOVER_TOOLS.some(t => localStorage.getItem(t.lsKey) === 'true');
  if (anyHover) {
    ensureTooltip();
    if (!window._dawnDebugHover) {
      document.addEventListener('mousemove', _debugMouseMove);
      document.addEventListener('mouseover', _debugMouseOver);
      window._dawnDebugHover = true;
    }
  } else {
    document.removeEventListener('mousemove', _debugMouseMove);
    document.removeEventListener('mouseover', _debugMouseOver);
    window._dawnDebugHover = false;
    document.getElementById(TOOLTIP_ID)?.remove();
  }
};

// ── DOM overlay tools ─────────────────────────────────────────────────────────
const BRKPT_ID = 'dawn-debug-breakpoint';

export const applyComponentBoundary = (on) => {
  const id = 'dawn-debug-component-boundary';
  document.getElementById(id)?.remove();
  if (on) {
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `[class*="-page"],[class*="-card"],[class*="-panel"],[class*="-modal"],[class*="-header"],[class*="-sidebar"],[class*="-section"],[class*="-container"]{outline:1px dashed rgba(236,72,153,0.5)!important;}`;
    document.head.appendChild(s);
  }
};

export const refreshTabOrder = () => {
  if (localStorage.getItem('dawn.debug.tabOrder') === 'true') applyTabOrder(true);
};

export const applyTabOrder = (on) => {
  document.querySelectorAll('[data-dawn-tabnum]').forEach(el => el.remove());
  if (on) {
    const focusable = [...document.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )];
    focusable.forEach((el, i) => {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      const lbl = document.createElement('span');
      lbl.setAttribute('data-dawn-tabnum', '1');
      lbl.className = 'dawn-debug-tabnum';
      lbl.textContent = i + 1;
      lbl.style.cssText = `position:fixed;z-index:999998;background:#4f46e5;color:#fff;font-size:9px;font-weight:700;padding:1px 4px;border-radius:3px;pointer-events:none;font-family:var(--font-mono);left:${rect.left}px;top:${rect.top}px;`;
      document.body.appendChild(lbl);
    });
  }
};

const BREAKPOINTS_LIST = [
  { name: 'XS', max: 480 }, { name: 'SM', max: 640 }, { name: 'MD', max: 768 },
  { name: 'LG', max: 1024 }, { name: 'XL', max: 1280 }, { name: '2XL', max: Infinity },
];

export const applyBreakpoint = (on) => {
  const existing = document.getElementById(BRKPT_ID);
  if (existing) {
    if (existing._onResize) window.removeEventListener('resize', existing._onResize);
    existing.remove();
  }
  if (on) {
    const el = document.createElement('div');
    el.id = BRKPT_ID;
    el.className = 'dawn-debug-breakpoint';
    const update = () => {
      const w = window.innerWidth;
      const bp = BREAKPOINTS_LIST.find(b => w <= b.max) || BREAKPOINTS_LIST[BREAKPOINTS_LIST.length - 1];
      el.textContent = `${bp.name} · ${w}px`;
    };
    update();
    el._onResize = update;
    window.addEventListener('resize', update);
    document.body.appendChild(el);
  }
};

export const applyGridFlex = (on) => {
  const id = 'dawn-debug-gridflex';
  document.getElementById(id)?.remove();
  document.querySelectorAll('[data-dawn-layout]').forEach(el => el.removeAttribute('data-dawn-layout'));
  if (on) {
    document.querySelectorAll('*').forEach(el => {
      const d = window.getComputedStyle(el).display;
      if      (d === 'flex'  || d === 'inline-flex')  el.setAttribute('data-dawn-layout', 'flex');
      else if (d === 'grid'  || d === 'inline-grid')  el.setAttribute('data-dawn-layout', 'grid');
    });
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `[data-dawn-layout="flex"]{outline:1px solid rgba(59,130,246,.6)!important;}[data-dawn-layout="grid"]{outline:1px solid rgba(16,185,129,.6)!important;}`;
    document.head.appendChild(s);
  }
};

const CLASS_PANEL_ID    = 'dawn-debug-class-panel';
const CLASS_HL_STYLE_ID = 'dawn-debug-class-hl';

export const applyClassInventory = (on) => {
  document.getElementById(CLASS_PANEL_ID)?.remove();
  document.getElementById(CLASS_HL_STYLE_ID)?.remove();
  if (!on) return;

  let activeClass = null;

  const scanClasses = () => {
    const panel = document.getElementById(CLASS_PANEL_ID);
    const counts = {};
    document.querySelectorAll('*').forEach(el => {
      if (panel && panel.contains(el)) return;
      el.classList.forEach(cls => { counts[cls] = (counts[cls] || 0) + 1; });
    });
    return Object.entries(counts).sort(([a, ca], [b, cb]) => cb - ca || a.localeCompare(b));
  };

  const setHighlight = (cls) => {
    document.getElementById(CLASS_HL_STYLE_ID)?.remove();
    activeClass = cls || null;
    if (!cls) return;
    const s = document.createElement('style');
    s.id = CLASS_HL_STYLE_ID;
    s.textContent = `.${CSS.escape(cls)}{outline:2px solid #f59e0b!important;outline-offset:1px!important;}`;
    document.head.appendChild(s);
  };

  const render = (filter = '') => {
    const list = document.getElementById('dawn-ci-list');
    const countEl = document.getElementById('dawn-ci-count');
    if (!list) return;
    const all = scanClasses();
    const q = filter.toLowerCase();
    const filtered = q ? all.filter(([cls]) => cls.toLowerCase().includes(q)) : all;
    const shown = filtered.slice(0, 300);
    list.innerHTML = shown.map(([cls, count]) => {
      const active = activeClass === cls;
      return `<div data-cls="${cls}" style="display:flex;justify-content:space-between;align-items:center;padding:3px 10px;cursor:pointer;border-radius:3px;font-family:var(--font-mono);font-size:11px;${active ? 'background:#1e293b' : ''}">` +
        `<span style="color:${active ? '#fbbf24' : '#e2e8f0'}">.${cls}</span>` +
        `<span style="color:#64748b;font-size:10px;margin-left:8px;flex-shrink:0">${count}</span>` +
      `</div>`;
    }).join('');
    if (countEl) countEl.textContent = filtered.length !== all.length ? `${filtered.length} / ${all.length}` : `${all.length} classes`;
    list.querySelectorAll('[data-cls]').forEach(row => {
      row.addEventListener('mouseenter', () => { if (activeClass !== row.dataset.cls) row.style.background = '#0f172a'; });
      row.addEventListener('mouseleave', () => { if (activeClass !== row.dataset.cls) row.style.background = ''; });
      row.addEventListener('click', () => {
        const cls = row.dataset.cls;
        setHighlight(activeClass === cls ? null : cls);
        render(document.getElementById('dawn-ci-filter')?.value || '');
      });
    });
  };

  const panel = document.createElement('div');
  panel.id = CLASS_PANEL_ID;
  panel.style.cssText = 'position:fixed;top:60px;right:12px;width:290px;max-height:calc(100vh - 80px);background:#1a1a2e;border:1px solid #334155;border-radius:8px;z-index:999997;display:flex;flex-direction:column;font-family:system-ui,sans-serif;box-shadow:0 8px 24px rgba(0,0,0,0.4);';
  panel.innerHTML =
    '<div style="padding:10px 12px;border-bottom:1px solid #334155;display:flex;align-items:center;gap:6px;flex-shrink:0">' +
      '<span style="color:#818cf8;font-weight:600;font-size:12px;flex:1">Class Inventory</span>' +
      '<span id="dawn-ci-count" style="color:#64748b;font-size:10px"></span>' +
      '<button id="dawn-ci-refresh" title="Re-scan" style="background:none;border:none;color:#64748b;cursor:pointer;font-size:14px;padding:2px 5px;border-radius:3px;line-height:1">↺</button>' +
      '<button id="dawn-ci-close" title="Close" style="background:none;border:none;color:#64748b;cursor:pointer;font-size:14px;padding:2px 4px;border-radius:3px;line-height:1">✕</button>' +
    '</div>' +
    '<div style="padding:8px 10px;border-bottom:1px solid #1e293b;flex-shrink:0">' +
      '<input id="dawn-ci-filter" placeholder="Filter classes…" style="width:100%;background:#0f172a;border:1px solid #334155;border-radius:4px;color:#e2e8f0;font-size:11px;padding:4px 8px;outline:none;box-sizing:border-box"/>' +
    '</div>' +
    '<div id="dawn-ci-list" style="overflow-y:auto;flex:1;padding:4px 0"></div>';

  document.body.appendChild(panel);
  render();

  document.getElementById('dawn-ci-filter').addEventListener('input', e => render(e.target.value));
  document.getElementById('dawn-ci-refresh').addEventListener('click', () => {
    setHighlight(null);
    render(document.getElementById('dawn-ci-filter')?.value || '');
  });
  document.getElementById('dawn-ci-close').addEventListener('click', () => {
    applyClassInventory(false);
    localStorage.setItem('dawn.debug.classInventory', 'false');
    window.dispatchEvent(new Event('dawn-debug-changed'));
  });
};

export const applyScreenRestrict = (on) => {
  const id = 'dawn-screen-restrict';
  document.getElementById(id)?.remove();
  if (on) {
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `#root{-webkit-user-select:none;user-select:none;}@media print{body{display:none!important;}}`;
    document.head.appendChild(s);
  }
};

export const syncOverlayTool = (key, on) => {
  if      (key === 'componentBoundary') applyComponentBoundary(on);
  else if (key === 'tabOrder')          applyTabOrder(on);
  else if (key === 'gridFlex')          applyGridFlex(on);
  else if (key === 'breakpoint')        applyBreakpoint(on);
  else if (key === 'classInventory')    applyClassInventory(on);
};

export const cleanupAllOverlays = () => {
  ['dawn-debug-component-boundary', 'dawn-debug-gridflex', 'dawn-screen-restrict', TOOLTIP_ID, CLASS_PANEL_ID, CLASS_HL_STYLE_ID].forEach(id => document.getElementById(id)?.remove());
  const bpEl = document.getElementById(BRKPT_ID);
  if (bpEl) { if (bpEl._onResize) window.removeEventListener('resize', bpEl._onResize); bpEl.remove(); }
  document.querySelectorAll('[data-dawn-tabnum]').forEach(el => el.remove());
  document.querySelectorAll('[data-dawn-layout]').forEach(el => el.removeAttribute('data-dawn-layout'));
  document.removeEventListener('mousemove', _debugMouseMove);
  document.removeEventListener('mouseover', _debugMouseOver);
  window._dawnDebugHover = false;
};
